import {Table} from "@mantine/core";
import type {BatchRow} from "../types";
import {BatchEntryRowChineseCharacter} from "./BatchEntryRowChineseCharacter";
import {BatchEntryRowChineseVocab} from "./BatchEntryRowChineseVocab";
import {BatchEntryRowJapaneseCharacter} from "./BatchEntryRowJapaneseCharacter";
import {BatchEntryRowJapaneseVocab} from "./BatchEntryRowJapaneseVocab";

function getRowComponent(languageCode: string, type: "vocab" | "character") {
  if (languageCode === "ja") {
    return type === "character"
      ? BatchEntryRowJapaneseCharacter
      : BatchEntryRowJapaneseVocab;
  }
  if (languageCode === "zh") {
    return type === "character"
      ? BatchEntryRowChineseCharacter
      : BatchEntryRowChineseVocab;
  }
  return type === "character" ? BatchEntryRowChineseCharacter : BatchEntryRowChineseVocab;
}

const JAPANESE_COLUMNS = {
  vocab: {field1: "Romaji", field2: "", field2Visible: false, auxiliary: "Notes / POS"},
  character: {
    field1: "On'yomi",
    field2: "Kun'yomi",
    field2Visible: true,
    auxiliary: "Notes / Radical",
  },
} as const;

const CHINESE_COLUMNS = {
  vocab: {
    field1: "Pinyin",
    field2: "",
    field2Visible: false,
    auxiliary: "Notes / Classifier",
  },
  character: {field1: "Pinyin", field2: "", field2Visible: false, auxiliary: "Notes"},
} as const;

const DEFAULT_COLUMNS = {
  field1: "Romaji",
  field2: "",
  field2Visible: false,
  auxiliary: "Notes",
} as const;

function getColumnHeaders(languageCode: string, type: "vocab" | "character") {
  const columns =
    languageCode === "ja"
      ? JAPANESE_COLUMNS[type]
      : languageCode === "zh"
        ? CHINESE_COLUMNS[type]
        : DEFAULT_COLUMNS;

  return [
    {key: "expression", label: type === "character" ? "Char" : "Expression", show: true},
    {key: "field1", label: columns.field1, show: true},
    {key: "field2", label: columns.field2, show: columns.field2Visible},
    {key: "meaning", label: "Meaning", show: true},
    {key: "auxiliary", label: columns.auxiliary, show: true},
  ];
}

export function BatchEntryTable(props: {
  rows: BatchRow[];
  type: "vocab" | "character";
  languageCode: string;
  onUpdateRow: (id: string, field: keyof BatchRow, value: string) => void;
  onRemoveRow: (id: string) => void;
}) {
  const {rows, type, languageCode, onUpdateRow, onRemoveRow} = props;

  const RowComponent = getRowComponent(languageCode, type);
  const columns = getColumnHeaders(languageCode, type);

  return (
    <Table striped withTableBorder>
      <Table.Thead>
        <Table.Tr>
          {columns
            .filter(col => col.show)
            .map(col => (
              <Table.Th key={col.key}>{col.label}</Table.Th>
            ))}
          <Table.Th style={{width: 50}} />
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {rows.map(row => (
          <RowComponent
            key={row.id}
            row={row}
            onUpdate={onUpdateRow}
            onRemove={onRemoveRow}
          />
        ))}
      </Table.Tbody>
    </Table>
  );
}
